import {
    columnModeNumColumns,
    emptyProducts,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/constants'
import { useItemStylesByColumn } from '@/features/pos/components/ProductCatalog/parts/ProductList/hooks/useItemStylesByColumn'
import { useMeasureNumColumns } from '@/features/pos/components/ProductCatalog/parts/ProductList/hooks/useMeasureNumColumns'
import { useRefreshOnFocus } from '@/features/pos/components/ProductCatalog/parts/ProductList/hooks/useRefreshOnFocus'
import { useScrollToSubcategory } from '@/features/pos/components/ProductCatalog/parts/ProductList/hooks/useScrollToSubcategory'
import ItemSeparator from '@/features/pos/components/ProductCatalog/parts/ProductList/parts/ItemSeparator'
import {
    ExtraData,
    Item,
    Mode,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/types'
import {
    getItemType,
    keyExtractor,
    overrideItemLayout,
    renderItem,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/utils'
import { ContentStyle, FlashList } from '@shopify/flash-list'
import { FC, memo, useRef } from 'react'

interface ProductListProps {
    items: readonly Item[]
    mode?: Mode
    subcategoryId?: string
    ListHeaderComponent?: FC
    contentContainerStyle?: ContentStyle
}

const ProductList: FC<ProductListProps> = ({
    items,
    mode = 'grid',
    subcategoryId,
    ListHeaderComponent,
    contentContainerStyle,
}) => {
    const [measuredNumColumns, measureLayout] = useMeasureNumColumns(mode)

    const ref = useRef<FlashList<Item>>(null)

    useScrollToSubcategory(ref, items, subcategoryId)

    const isColumn = mode === 'column'

    useRefreshOnFocus(ref, isColumn)

    const getNumColumns = () => {
        if (isColumn) return columnModeNumColumns

        return measuredNumColumns ?? 1
    }
    const numColumns = getNumColumns()

    const getData = () => {
        if (isColumn) return items

        return measuredNumColumns ? items : emptyProducts
    }

    const itemStylesByColumn = useItemStylesByColumn(numColumns, mode)

    const extraData: ExtraData = { numColumns, mode, itemStylesByColumn }

    return (
        <FlashList
            key={numColumns}
            data={getData()}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            numColumns={numColumns}
            estimatedItemSize={69}
            extraData={extraData}
            getItemType={getItemType}
            onLayout={measureLayout}
            overrideItemLayout={overrideItemLayout}
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={ItemSeparator}
            ListHeaderComponent={ListHeaderComponent}
            contentContainerStyle={contentContainerStyle}
            ref={ref}
        />
    )
}

export default memo(ProductList)
